import { ReactNode } from 'react'
import { Box, BoxProps, styled, Theme } from '@mui/material'
import { ICON_SIZE_LARGE, ICON_SIZE_MEDIUM } from '@carto/meridian-ds/theme'
import BigQueryIcon from 'assets/icons/connectors/bigquery-white.svg?react'
import SnowflakeIcon from 'assets/icons/connectors/snowflake-white.svg?react'
import PostgresIcon from 'assets/icons/connectors/postgres-white.svg?react'
import PostgresMonoIcon from 'assets/icons/connectors/postgres-simple.svg?react'
import RedshiftIcon from 'assets/icons/connectors/redshift.svg?react'
import RedshiftMonoIcon from 'assets/icons/connectors/redshift-simple.svg?react'
import CartoIcon from 'assets/icons/connectors/carto.svg?react'
import DatabricksIcon from 'assets/icons/connectors/databricks-white.svg?react'
import CartoDWIcon from 'assets/icons/connectors/carto-dw.svg?react'
import OracleIcon from 'assets/icons/connectors/oracle.svg?react'
import { ProviderType } from 'features/connections/types'

function colorProps(theme: Theme, provider?: ProviderType): { backgroundColor: string } {
  const colorMap = {
    [ProviderType.BIGQUERY]: '#669ef6',
    [ProviderType.POSTGRES]: '#326790',
    [ProviderType.SNOWFLAKE]: '#28b4e8',
    [ProviderType.REDSHIFT]: '#b1d3f2',
    [ProviderType.DATABRICKS]: '#fe4f02',
    [ProviderType.DATABRICKS_REST]: '#fe4f02',
    [ProviderType.CARTO_DW]: '#046fe2',
    [ProviderType.CARTO]: '#ebf4fd',
    [ProviderType.ORACLE]: '#ffffff',
    default: theme.palette.primary.relatedLight
  }

  const color = provider ? colorMap[provider] : colorMap.default
  return { backgroundColor: color }
}

const Frame = styled(Box, {
  shouldForwardProp: (prop: string) => !['size', 'isMonochrome', 'provider'].includes(prop)
})<{
  isMonochrome?: boolean
  size?: string
  provider?: ProviderType
}>(({ isMonochrome, size, provider, theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: theme.spacing(4.5),
  height: theme.spacing(4.5),
  borderRadius: theme.shape.borderRadius,
  ...colorProps(theme, provider),

  '& svg, & img': {
    width: ICON_SIZE_LARGE,
    height: ICON_SIZE_LARGE
  },

  ...(isMonochrome && {
    backgroundColor: 'transparent !important',

    '& svg path': {
      fill: `${theme.palette.text.primary} !important`
    }
  }),

  ...(size === 'small' && {
    width: ICON_SIZE_LARGE,
    height: ICON_SIZE_LARGE,

    '& svg, & img': {
      width: ICON_SIZE_MEDIUM,
      height: ICON_SIZE_MEDIUM
    }
  })
}))

export const ICONS = {
  bigquery: <BigQueryIcon aria-label="bigquery icon" />,
  snowflake: <SnowflakeIcon aria-label="snowflake icon" />,
  postgres: <PostgresIcon aria-label="postgresql icon" />,
  postgres_mono: <PostgresMonoIcon aria-label="postgresql icon" />,
  redshift: <RedshiftIcon aria-label="redshift icon" />,
  redshift_mono: <RedshiftMonoIcon aria-label="redshift icon" />,
  carto: <CartoIcon aria-label="carto icon" />,
  carto_dw: <CartoDWIcon aria-label="carto dw icon" />,
  databricks: <DatabricksIcon aria-label="databricks icon" />,
  databricksRest: <DatabricksIcon aria-label="databricks icon" />,
  oracle: <OracleIcon aria-label="oracle icon" />
}

interface ProviderIconProps {
  provider?: ProviderType
  size?: string
  icon?: ReactNode
  monochrome?: boolean
}

export default function ProviderIcon({ provider, size, icon, monochrome, ...boxProps }: ProviderIconProps & BoxProps) {
  const iconElement = provider
    ? provider === ProviderType.REDSHIFT && monochrome
      ? ICONS['redshift_mono']
      : provider === ProviderType.POSTGRES && monochrome
      ? ICONS['postgres_mono']
      : ICONS[provider]
    : icon

  return (
    <Frame {...boxProps} isMonochrome={monochrome} size={size} provider={provider}>
      {iconElement}
    </Frame>
  )
}
